// src/services/notification.service.ts
import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';
import { 
  Notification, 
  AdminNotification, 
  CreateAdminNotificationDto, 
  AdminNotificationListResponse, 
  ExpertClientNotification, 
  CreateExpertNotificationDto, 
  ExpertClientNotificationsResponse,
  ExpertBulkNotification,
  ExpertBulkNotificationsResponse,
  CreateExpertBulkNotificationDto,
  NotificationScenario
} from '../types/notification';

// Query parameters for getting notifications
export interface GetNotificationsParams {
  page?: number;
  limit?: number;
  status?: 'read' | 'unread' | 'all';
  level?: 'info' | 'warning' | 'danger';
  source?: 'system' | 'expert';
  search?: string;
}

// --- НОВИЙ ІНТЕРФЕЙС ДЛЯ РЕЗУЛЬТАТУ getNotifications ---
// Цей інтерфейс представляє те, що функція getNotifications буде повертати
export interface GetNotificationsResult {
  notifications: Notification[]; // Масив нотифікацій
  meta: {
    total: number;
    page: number;
    limit: number;
    timestamp?: string;
    path?: string;
    method?: string;
    statusCode?: number;
    responseTime?: number;
  };
}

// Получить список уведомлений с фильтрацией и пагинацией
export const getNotifications = async (params: GetNotificationsParams = {}): Promise<GetNotificationsResult> => {
  const axiosResponse = await api.get<ApiResponse<Notification[]>>('/notifications', { params });
  const { data: notificationsArray, meta } = axiosResponse.data;

  return {
    notifications: notificationsArray || [],
    meta: {
      total: meta?.total || 0,
      page: meta?.page || 1,
      limit: meta?.limit || params.limit || 10,
      timestamp: meta?.timestamp,
      path: meta?.path,
      method: meta?.method,
      statusCode: meta?.statusCode,
      responseTime: meta?.responseTime,
    }
  };
};

// Получить детали конкретного уведомления
export const getNotificationById = async (id: string): Promise<Notification> => {
  const response = await api.get<ApiResponse<Notification>>(`/notifications/${id}`);
  return unwrapApiResponse<Notification>(response);
};

// Отметить уведомление как прочитанное
export const markNotificationAsRead = async (id: string): Promise<void> => {
  await api.put(`/notifications/${id}/read`);
};

// Отметить все уведомления как прочитанные
export const markAllNotificationsAsRead = async (): Promise<void> => {
  await api.put('/notifications/mark-all-read');
};

// --- ADMIN METHODS ---

export const getAdminNotifications = async (params: GetNotificationsParams = {}): Promise<AdminNotificationListResponse> => {
  const response = await api.get<ApiResponse<AdminNotification[]>>('/admin/notifications', { params });
  const { data, meta } = response.data;
  return {
    data: data,
    meta: {
      total: meta?.total || 0,
      page: meta?.page || 1,
      limit: meta?.limit || 10,
    }
  };
};

export const createAdminNotification = async (data: CreateAdminNotificationDto): Promise<AdminNotification> => {
  const response = await api.post<ApiResponse<AdminNotification>>('/admin/notifications/send', data);
  return unwrapApiResponse<AdminNotification>(response);
};

// --- EXPERT CLIENT NOTIFICATIONS ---

export const getExpertClientNotifications = async (
  clientId: number,
  params: { page?: number; limit?: number; status?: 'all' | 'read' | 'unread' } = {}
): Promise<ExpertClientNotificationsResponse> => {
  const response = await api.get(`/expert/clients/${clientId}/notifications`, { params });
  const { data, meta } = response.data;
  return {
    data: data || [],
    meta: { total: meta?.total || 0, page: meta?.page || 1, limit: meta?.limit || 10 },
  };
};

export const sendExpertClientNotification = async (
  clientId: number,
  data: CreateExpertNotificationDto
): Promise<ExpertClientNotification> => {
  const response = await api.post<ApiResponse<ExpertClientNotification>>(
    `/expert/clients/${clientId}/notifications`,
    data
  );
  return unwrapApiResponse<ExpertClientNotification>(response);
};

// --- EXPERT BULK NOTIFICATIONS ---

export const getExpertBulkNotifications = async (params: { page?: number; limit?: number } = {}): Promise<ExpertBulkNotificationsResponse> => {
  // Backend returns { success: true, data: { data: ExpertBulkNotification[], total: number } }
  const response = await api.get<ApiResponse<{ data: ExpertBulkNotification[], total: number }>>('/expert/bulk-notifications', { params });
  const unwrapped = unwrapApiResponse<{ data: ExpertBulkNotification[], total: number }>(response);
  
  return {
    data: unwrapped.data || [],
    meta: { 
      total: unwrapped.total || 0, 
      page: params.page || 1, 
      limit: params.limit || 10 
    },
  };
};

export const sendExpertBulkNotification = async (data: CreateExpertBulkNotificationDto): Promise<ExpertBulkNotification> => {
  const response = await api.post<ApiResponse<ExpertBulkNotification>>('/expert/bulk-notifications', data);
  return unwrapApiResponse<ExpertBulkNotification>(response);
};

export const getExpertBulkNotificationPreview = async (params: { targetType: 'all' | 'groups'; groupId?: number }): Promise<{ id: number; name: string }[]> => {
  const response = await api.get<ApiResponse<{ id: number; name: string }[]>>('/expert/bulk-notifications/preview', { params });
  return unwrapApiResponse<{ id: number; name: string }[]>(response);
};

// Каталог сценаріїв сповіщень (адмінка)
export const getNotificationCatalog = async (): Promise<NotificationScenario[]> => {
  const response = await api.get<ApiResponse<NotificationScenario[]>>('/admin/notifications/catalog');
  return response.data.data || [];
};

export const setNotificationScenarioEnabled = async (scenario: string, enabled: boolean): Promise<NotificationScenario> => {
  const response = await api.patch<ApiResponse<NotificationScenario>>(`/admin/notifications/catalog/${scenario}`, { enabled });
  return response.data.data;
};

// Старий вимикач по PushType (system-setting `push`) — лишився лише для повторного увімкнення з каталогу.
export const setLegacyPushTypeEnabled = async (pushType: string, enabled: boolean): Promise<void> => {
  await api.post('/admin/system-settings/push', { value: { [pushType]: enabled } });
};
